/**
 * MatchCompletedBanner — shown in ScorerConsole when both innings are done.
 * Also hosts the Match Awards confirmation UX (Player of the Match etc.).
 */
import { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';

export default function MatchCompletedBanner({ innings, fixture, eventId, fixtureId }) {
  const [liveScore, setLiveScore] = useState(null);
  const [awards, setAwards] = useState([]);
  const [loadingAwards, setLoadingAwards] = useState(false);
  const [editingType, setEditingType] = useState(null); // award_type being overridden
  const [overridePlayerId, setOverridePlayerId] = useState('');
  const [squadsData, setSquadsData] = useState(null);

  const loadAwards = useCallback(async () => {
    try {
      setLoadingAwards(true);
      const res = await axiosInstance.get(`/v1/events/${eventId}/fixtures/${fixtureId}/awards`);
      setAwards(res.data || []);
    } catch { /* noop */ }
    finally { setLoadingAwards(false); }
  }, [eventId, fixtureId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/score`);
        if (!cancelled) setLiveScore(res.data);
      } catch { /* noop */ }
      try {
        const sq = await axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/squads`);
        if (!cancelled) setSquadsData(sq.data || null);
      } catch { /* noop */ }
      loadAwards();
    })();
    return () => { cancelled = true; };
  }, [eventId, fixtureId, loadAwards]);

  const resultSummary = liveScore?.match_situation
    || fixture?.result_summary
    || 'Match completed';
  const winnerName = fixture?.winner_team_id === fixture?.team_a_id
    ? fixture?.team_a_name
    : fixture?.winner_team_id === fixture?.team_b_id
      ? fixture?.team_b_name
      : null;

  const allPlayers = (squadsData?.teams || []).flatMap(t =>
    (t.players || []).map(p => ({
      id: p.player_pool_id,
      player_name: p.player_name,
      team_name: t.team_name,
    }))
  );

  const handleRegenerate = async () => {
    try {
      await axiosInstance.post(`/v1/events/${eventId}/fixtures/${fixtureId}/awards/auto-generate`);
      toast.success('Match awards re-calculated');
      loadAwards();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to re-calculate awards');
    }
  };

  const handleConfirm = async (awardType, body) => {
    try {
      await axiosInstance.post(
        `/v1/events/${eventId}/fixtures/${fixtureId}/awards/${awardType}/confirm`,
        body,
      );
      toast.success('Award confirmed');
      setEditingType(null);
      setOverridePlayerId('');
      loadAwards();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to confirm award');
    }
  };

  return (
    <div className="space-y-4" data-testid="match-completed-banner">
      {/* Result banner */}
      <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-center shadow-md">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/20 mb-3">
          <i className="ph-fill ph-trophy text-white text-2xl"></i>
        </div>
        <h3 className="text-white font-bold text-xl mb-1" data-testid="match-completed-title">
          Match Completed
        </h3>
        {winnerName && (
          <p className="text-white/90 text-sm mb-2">
            Winner: <span className="font-bold">{winnerName}</span>
          </p>
        )}
        <p className="text-white/90 text-sm italic">{resultSummary}</p>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
          {innings.map(inn => (
            <div
              key={inn.id}
              className="bg-white/15 rounded-lg px-3 py-2 text-left backdrop-blur"
              data-testid={`innings-summary-${inn.innings_number}`}
            >
              <div className="text-[10px] uppercase tracking-wide text-white/70">
                {inn.kind === 'super_over' ? 'Super over' : `Innings ${inn.innings_number}`}
              </div>
              <div className="text-sm font-semibold text-white truncate">
                {inn.batting_team_name}
              </div>
              <div className="text-lg font-bold text-white">
                {inn.total_runs}/{inn.total_wickets}
                <span className="text-xs text-white/70 ml-1">({inn.total_overs} ov)</span>
              </div>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-white/70 mt-4 italic">
          No further balls can be recorded. Edits are locked.
        </p>
      </div>

      {/* Match Awards — scorer confirmation UX */}
      <div
        className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4"
        data-testid="match-awards-section"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <i className="ph-fill ph-trophy text-amber-600 text-lg"></i>
            <h4 className="text-sm font-bold text-amber-900 uppercase tracking-wider">
              Match Awards
            </h4>
          </div>
          <button
            onClick={handleRegenerate}
            className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
            data-testid="regenerate-match-awards-btn"
          >
            <i className="ph-bold ph-arrows-clockwise"></i> Re-suggest
          </button>
        </div>
        {loadingAwards ? (
          <div className="text-xs text-gray-500">Loading awards…</div>
        ) : awards.length === 0 ? (
          <div className="text-xs text-gray-500">
            No match awards yet — tap "Re-suggest" to auto-pick.
          </div>
        ) : (
          <div className="space-y-2">
            {awards.map(a => (
              <AwardRow
                key={a.id}
                award={a}
                players={allPlayers}
                isEditing={editingType === a.award_type}
                onEdit={() => {
                  setEditingType(a.award_type);
                  setOverridePlayerId(a.winner_player_id || '');
                }}
                onCancelEdit={() => { setEditingType(null); setOverridePlayerId(''); }}
                overridePlayerId={overridePlayerId}
                setOverridePlayerId={setOverridePlayerId}
                onConfirm={(body) => handleConfirm(a.award_type, body)}
                onUnfinalize={() => handleConfirm(a.award_type, { is_finalized: false })}
              />
            ))}
          </div>
        )}
        <p className="text-[11px] text-amber-700/70 mt-3 italic">
          Awards appear on the public scorecard once you tap <strong>Confirm</strong>.
        </p>
      </div>
    </div>
  );
}

function AwardRow({
  award, players, isEditing, onEdit, onCancelEdit,
  overridePlayerId, setOverridePlayerId, onConfirm, onUnfinalize,
}) {
  const status = award.is_finalized
    ? { label: 'Confirmed', tone: 'bg-emerald-100 text-emerald-700' }
    : { label: 'Suggested', tone: 'bg-amber-100 text-amber-700' };

  return (
    <div
      data-testid={`match-award-row-${award.award_type}`}
      className="rounded-lg border border-gray-100 bg-white p-3"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
          <i className={`ph-bold ${award.icon || 'ph-medal'}`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-[#2B3A67]">{award.title}</span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${status.tone}`}>
              {status.label}
            </span>
          </div>
          <div className="text-sm text-gray-800 mt-0.5 truncate">
            {award.winner_player_name || '—'}
            {award.winner_team_name && (
              <span className="text-xs text-gray-500"> · {award.winner_team_name}</span>
            )}
          </div>
          {award.stat_value && (
            <div className="text-[11px] text-gray-500 mt-0.5">{award.stat_value}</div>
          )}

          {isEditing && (
            <div className="mt-2 space-y-2">
              <select
                value={overridePlayerId}
                onChange={e => setOverridePlayerId(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5"
                data-testid={`override-player-${award.award_type}`}
              >
                <option value="">— Keep current pick —</option>
                {players.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.player_name} ({p.team_name})
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onConfirm({
                    winner_player_id: overridePlayerId || undefined,
                    is_finalized: true,
                  })}
                  className="flex-1 py-1.5 px-3 rounded-lg bg-[#3AAFA9] text-white text-sm font-semibold hover:bg-[#2D8A85]"
                  data-testid={`save-award-${award.award_type}`}
                >
                  Confirm
                </button>
                <button
                  onClick={onCancelEdit}
                  className="py-1.5 px-3 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >Cancel</button>
              </div>
            </div>
          )}

          {!isEditing && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {!award.is_finalized ? (
                <>
                  <button
                    onClick={() => onConfirm({ is_finalized: true })}
                    className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-amber-600 text-white hover:bg-amber-700"
                    data-testid={`confirm-award-${award.award_type}`}
                  >Confirm</button>
                  <button
                    onClick={onEdit}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-100"
                    data-testid={`override-award-${award.award_type}`}
                  >Override player</button>
                </>
              ) : (
                <>
                  <button
                    onClick={onEdit}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                    data-testid={`edit-award-${award.award_type}`}
                  >Edit</button>
                  <button
                    onClick={onUnfinalize}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-lg text-gray-400 hover:text-gray-600"
                  >Un-confirm</button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
